/**
 * Barcode / QR code scanner using BarcodeDetector API
 * (native on Android Chrome, ZXing-WASM polyfill on iOS/other browsers).
 */

import { BarcodeDetector as ZXingBarcodeDetector } from 'barcode-detector/pure';

// Use native BarcodeDetector (Android Chrome) when available; WASM fallback otherwise
const BarcodeDetectorImpl = ('BarcodeDetector' in window)
  ? window.BarcodeDetector
  : ZXingBarcodeDetector;

const FORMATS = ['code_128', 'code_39', 'ean_13', 'qr_code', 'ean_8', 'upc_a', 'code_93'];

let active = null; // { stream, rafId, video, overlay }

/**
 * @param {string} elementId  - ID of the container element to attach the camera to
 * @param {function} onScan   - Called with the raw scanned string; fires once then stops
 * @param {object} [options]
 * @param {boolean} [options.narrow=false] - Restrict detection to center-third strip
 */
export async function startScanner(elementId, onScan, options = {}) {
  await stopScanner();

  const container = document.getElementById(elementId);
  if (!container) return;

  try {
    const detector = new BarcodeDetectorImpl({ formats: FORMATS });

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width:  { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });

    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    container.appendChild(video);
    await video.play();

    // Narrow-mode canvas (created once, reused each frame)
    let canvas = null;
    let ctx = null;
    if (options.narrow) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d');
    }

    // Narrow-mode visual overlay
    let overlay = null;
    if (options.narrow) {
      overlay = document.createElement('div');
      overlay.className = 'scanner-narrow-overlay';
      overlay.innerHTML = `
        <div class="scanner-narrow-top"></div>
        <div class="scanner-narrow-line"></div>
        <div class="scanner-narrow-bottom"></div>
      `;
      container.style.position = 'relative';
      container.appendChild(overlay);
    }

    let lastTs = 0;
    let fired = false;

    function loop(ts) {
      if (fired) return;
      active.rafId = requestAnimationFrame(loop);

      // Throttle to ~15 fps so detect() doesn't pile up
      if (ts - lastTs < 66) return;
      lastTs = ts;

      let source;
      if (options.narrow && canvas && video.readyState >= 2 && video.videoWidth > 0) {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const stripH = Math.floor(vh / 3);
        canvas.width = vw;
        canvas.height = stripH;
        // Draw only the middle third of the video into the canvas
        ctx.drawImage(video, 0, Math.floor(vh / 3), vw, stripH, 0, 0, vw, stripH);
        source = canvas;
      } else {
        source = video;
      }

      detector.detect(source).then(results => {
        if (fired || !results.length) return;
        fired = true;
        onScan(results[0].rawValue);
      }).catch(() => {});
    }

    active = { stream, video, rafId: null, overlay };
    active.rafId = requestAnimationFrame(loop);

  } catch (err) {
    console.warn('Scanner start failed:', err);
    active = null;
  }
}

export async function stopScanner() {
  if (!active) return;
  const { stream, video, rafId, overlay } = active;
  active = null;
  if (rafId) cancelAnimationFrame(rafId);
  stream?.getTracks().forEach(t => t.stop());
  video?.remove();
  overlay?.remove();
}
